import React, { useState, useEffect } from 'react';
import pb from '../services/pocketbase';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { CITIES } from '../services/cities';
import '../styles/Chats.css';

const ADMIN_ID = '33ev0wtnwg7vdvs';

function Chats() {
  const [activeTab, setActiveTab] = useState('private');
  const navigate = useNavigate();
  const currentUser = pb.authStore.model;
  const currentUserRole = currentUser?.userRole;
  const [adminUser, setAdminUser] = useState(null);
  const [privateChats, setPrivateChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);

  const isVolunteer = currentUserRole === 'volunteer' || currentUserRole === 'volunteer_pending';

  const userCity = currentUser?.city;
  const cityObj = CITIES.find(c => c.id === userCity);

  const groupChats = [
    { id: 'general', name: 'Общий чат', icon: '🌐', description: 'Для всех пользователей', forAll: true },
    ...(cityObj ? [{ id: `city_${userCity}`, name: `Чат — ${cityObj.name}`, icon: '📍', description: `Жители и районы ${cityObj.name} (+100 км)`, forAll: true, isCity: true }] : []),
    { id: 'volunteers', name: 'Чат волонтёров', icon: '🤝', description: 'Координация помощи', allowedTypes: ['volunteer', 'volunteer_pending'] },
    { id: 'veterinary', name: 'Чат ветеринаров', icon: '🏥', description: 'Профессиональное общение', allowedType: 'veterinary' },
    { id: 'trainers', name: 'Чат кинологов', icon: '🎓', description: 'Обмен опытом', allowedType: 'trainer' },
    { id: 'groomers', name: 'Чат грумеров', icon: '✂️', description: 'Советы и рекомендации', allowedType: 'groomer' },
    { id: 'zootaxi', name: 'Чат зоотакси', icon: '🚗', description: 'Координация перевозок', allowedType: 'zootaxi' },
  ].filter(c => {
    if (c.forAll) return true;
    if (c.allowedTypes) return c.allowedTypes.includes(currentUserRole);
    return c.allowedType === currentUserRole;
  });

  // Загружаем имя админа один раз
  useEffect(() => {
    if (currentUser?.id === ADMIN_ID) return; // сам админ не видит себя
    pb.collection('users').getOne(ADMIN_ID, { fields: 'id,name,username' })
      .then(u => setAdminUser(u))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'private') fetchPrivateChats();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrivateChats = async () => {
    setChatsLoading(true);
    try {
      const records = await pb.collection('messages').getList(1, 200, {
        filter: `(userId = "${currentUser?.id}" || chatId ~ "${currentUser?.id}") && chatType = "private"`,
        sort: '-created'
      });

      // Берём последнее сообщение по каждому chatId
      const chatsMap = {};
      for (const msg of records.items) {
        if (!chatsMap[msg.chatId]) {
          chatsMap[msg.chatId] = msg;
        }
      }

      const chatEntries = Object.values(chatsMap);

      // Собираем все ID собеседников одним батч-запросом
      const otherUserIds = chatEntries
        .map(msg => msg.chatId.split('_').find(uid => uid !== currentUser?.id))
        .filter(Boolean);

      const uniqueIds = [...new Set(otherUserIds)];
      const usersMap = {};

      if (uniqueIds.length > 0) {
        try {
          const filter = uniqueIds.map(id => `id = "${id}"`).join(' || ');
          const users = await pb.collection('users').getFullList({
            filter,
            fields: 'id,name,username'
          });
          for (const u of users) {
            usersMap[u.id] = u.name || u.username || 'Пользователь';
          }
        } catch (e) {}
      }

      const chatsList = chatEntries.map(msg => {
        const otherUserId = msg.chatId.split('_').find(uid => uid !== currentUser?.id);
        const userName = usersMap[otherUserId] || 'Пользователь';

        return {
          id: msg.chatId,
          userName,
          otherUserId,
          lastMessage: msg.text,
          time: new Date(msg.created).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          unread: 0
        };
      });

      setPrivateChats(chatsList);
    } catch (e) {
      console.error(e);
    } finally {
      setChatsLoading(false);
    }
  };

  return (
    <div className="chats-page">
      <div className="header">
        <div className="header-top">
          <div className="header-title">💬 Сообщения</div>
          <div className="search-icon">🔍</div>
        </div>
        <div className="tabs">
          <div className={`tab ${activeTab === 'private' ? 'active' : ''}`} onClick={() => setActiveTab('private')}>
            Личные
          </div>
          <div className={`tab ${activeTab === 'group' ? 'active' : ''}`} onClick={() => setActiveTab('group')}>
            Чаты ({groupChats.length})
          </div>
        </div>
      </div>

      <div className="content">
        {activeTab === 'group' && (
          <div className="group-chats-list">
            {groupChats.map(chat => (
              <div key={chat.id} className="chat-item group-chat-item"
                onClick={() => navigate(`/chat/${chat.id}`, { state: { chatType: 'group', chatName: chat.name } })}>
                <div className="chat-avatar group-avatar">{chat.icon}</div>
                <div className="chat-content">
                  <div className="chat-header">
                    <div className="chat-name">{chat.name}</div>
                    {chat.allowedType && <div className="access-badge">🔒 Закрытый</div>}
                  </div>
                  <div className="chat-message">{chat.description}</div>
                </div>
                <div className="chat-arrow">→</div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'private' && (
          <div className="private-chats-list">
            {/* Закреплённая карточка админа — всегда первая */}
            {adminUser && currentUser?.id !== ADMIN_ID && (
              <div
                className="chat-item"
                onClick={() => {
                  const chatId = [currentUser.id, ADMIN_ID].sort().join('_');
                  navigate(`/chat/${chatId}`, { state: { chatType: 'private', chatName: adminUser.name || 'Админ' } });
                }}
                style={{ borderLeft: '4px solid #3B5998', background: '#F5F9FF' }}
              >
                <div className="chat-avatar" style={{ background: 'linear-gradient(135deg, #1a237e, #3B5998)' }}>👑</div>
                <div className="chat-content">
                  <div className="chat-header">
                    <div className="chat-name">{adminUser.name || 'Админ'}</div>
                    <div style={{ fontSize: '10px', background: '#3B5998', color: 'white', borderRadius: '8px', padding: '2px 6px', fontWeight: '700' }}>ADMIN</div>
                  </div>
                  <div className="chat-message">Администратор AWOO · Написать сообщение</div>
                </div>
                <div className="chat-arrow">→</div>
              </div>
            )}
            {chatsLoading ? (
              <div style={{textAlign:'center', padding:'40px', color:'#999'}}>Загрузка...</div>
            ) : privateChats.length === 0 ? (
              <div style={{textAlign:'center', padding:'40px 20px', color:'#999'}}>
                <div style={{fontSize:'48px'}}>💬</div>
                <p>Нет личных сообщений</p>
                <p style={{fontSize:'13px'}}>Напишите кому-нибудь из объявления</p>
              </div>
            ) : (
              privateChats.map(chat => (
                <div key={chat.id} className="chat-item"
                  onClick={() => navigate(`/chat/${chat.id}`, { state: { chatType: 'private', chatName: chat.userName } })}>
                  <div className="chat-avatar">👤</div>
                  <div className="chat-content">
                    <div className="chat-header">
                      <div className="chat-name">{chat.userName}</div>
                      <div className="chat-time">{chat.time}</div>
                    </div>
                    <div className="chat-message">{chat.lastMessage}</div>
                  </div>
                  {chat.unread > 0 && <div className="unread-badge">{chat.unread}</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default Chats;
